import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { MenuStackParamList } from '@navigation'
import { Share } from 'react-native'
import Clipboard from '@react-native-clipboard/clipboard'
import { useGetProfile, useScreen } from '@hooks'
import { useLanguage } from '@context'
import { showErrorToast, showSuccessToast, WEB_APP_URL } from '@utils'

// id du conteneur du QR (web) : permet de cibler précisément son <svg> au
// téléchargement, sans confondre avec les autres SVG de la page.
export const QR_CONTAINER_ID = 'keeptouch-qr-code'

interface UseQRCodeResult {
  navigation: NativeStackNavigationProp<MenuStackParamList>
  userName: string
  profileUrl: string
  onShareProfile: () => void
  onCopyLink: () => void
  onDownload: () => void
}

export const useQRCode = (): UseQRCodeResult => {
  const navigation = useNavigation<NativeStackNavigationProp<MenuStackParamList>>()
  const { data: profile } = useGetProfile()
  const { t } = useLanguage()
  const { isWeb } = useScreen()

  const generateUserName = (name: string): string => {
    if (!name) return '@user'
    return `@${name.toLowerCase().replace(/\s+/g, '_')}`
  }

  const userName = generateUserName(profile?.fullName || '')
  // Shareable link to the web app's `profile/:profileId` route. That route (and
  // GET /profile/:id behind it) looks profiles up by ACCOUNT id: `profile.id` is
  // the profile row id and opened an endless loading screen for the recipient.
  const profileUrl = `${WEB_APP_URL}/profile/${profile?.accountId ?? ''}`

  const onShareProfile = async () => {
    if (isWeb) {
      try {
        if (navigator.share) {
          await navigator.share({
            title: t('qrCode.shareTitle'),
            text: t('qrCode.shareMessage').replace('{url}', profileUrl),
            url: profileUrl,
          })
        } else {
          navigator.clipboard.writeText(profileUrl)
          showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
        }
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    } else {
      try {
        await Share.share({
          message: t('qrCode.shareMessage').replace('{url}', profileUrl),
          url: profileUrl,
          title: t('qrCode.shareTitle'),
        })
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    }
  }

  // Copie web fiable : l'API navigator.clipboard échoue hors contexte sécurisé
  // (ou si le focus est perdu). On retombe alors sur un textarea + execCommand,
  // qui fonctionne partout. Renvoie true si la copie a réussi.
  const copyOnWeb = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch {
      // on tente le repli ci-dessous
    }
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(textarea)
      return ok
    } catch {
      return false
    }
  }

  const onCopyLink = async () => {
    if (isWeb) {
      const ok = await copyOnWeb(profileUrl)
      if (ok) {
        showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
      } else {
        showErrorToast(t('qrCode.error'), t('qrCode.copyError'))
      }
    } else {
      Clipboard.setString(profileUrl)
      showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
    }
  }

  // Déclenche le téléchargement d'un fichier dans le navigateur.
  const triggerWebDownload = (href: string, filename: string) => {
    const link = document.createElement('a')
    link.href = href
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onDownload = async () => {
    if (isWeb) {
      try {
        // On cible précisément le SVG du QR (via l'id du conteneur), et NON le
        // premier svg de la page — l'ancien sélecteur récupérait une icône du menu.
        const container = document.getElementById(QR_CONTAINER_ID)
        const svgElement = container?.querySelector('svg') as SVGSVGElement | null
        if (!svgElement) {
          showErrorToast(t('qrCode.error'), t('qrCode.downloadError'))
          return
        }
        const svgString = new XMLSerializer().serializeToString(svgElement)
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
        const svgUrl = URL.createObjectURL(svgBlob)
        const fileBase = `keeptouch-qrcode-${userName.replace('@', '')}`

        // On rastérise le SVG en PNG (format universel, facile à partager).
        // En cas d'échec, on retombe sur le téléchargement du SVG.
        const img = new Image()
        img.onload = () => {
          try {
            const size = 1024
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')
            if (!ctx) throw new Error('no-2d-context')
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, size, size)
            ctx.drawImage(img, 0, 0, size, size)
            URL.revokeObjectURL(svgUrl)
            canvas.toBlob((blob) => {
              if (!blob) {
                triggerWebDownload(svgUrl, `${fileBase}.svg`)
              } else {
                const pngUrl = URL.createObjectURL(blob)
                triggerWebDownload(pngUrl, `${fileBase}.png`)
                URL.revokeObjectURL(pngUrl)
              }
              showSuccessToast(t('qrCode.success'), t('qrCode.downloaded'))
            }, 'image/png')
          } catch {
            triggerWebDownload(svgUrl, `${fileBase}.svg`)
            showSuccessToast(t('qrCode.success'), t('qrCode.downloaded'))
          }
        }
        img.onerror = () => {
          // Repli : téléchargement direct du SVG.
          triggerWebDownload(svgUrl, `${fileBase}.svg`)
          showSuccessToast(t('qrCode.success'), t('qrCode.downloaded'))
        }
        img.src = svgUrl
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.downloadError'))
      }
    } else {
      // Native: use Share to save/share the QR code URL
      try {
        await Share.share({
          message: profileUrl,
          title: t('qrCode.shareTitle'),
        })
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    }
  }

  return {
    navigation,
    userName,
    profileUrl,
    onShareProfile,
    onCopyLink,
    onDownload,
  }
}
